import React from "react";
import { cn } from "../lib/utils.js";
import { DisclosurePanel } from "./DisclosurePanel.js";
import { DisclosureRoot } from "./DisclosureRoot.js";
import { DisclosureTrigger } from "./DisclosureTrigger.js";

/* Disclosure — one accessible show/hide with an animated panel (ADR-0091).

   The app grew three ways of doing this: a bare native `<details>` (which can
   never animate its panel), a hand-rolled `aria-expanded` pair that renders
   `{open && <div>}` (which can never animate its exit), and a caret that is
   SWAPPED between two chevron glyphs (which can never animate at all, because
   the two icons are different elements).

   The panel opens on a 0fr → 1fr grid row, so it animates to its own height
   with nobody measuring anything, and it collapses the same way on close.

   Composed from DisclosureRoot, DisclosureTrigger and DisclosurePanel. Reach
   for those parts directly when the trigger and the panel sit apart. */

export interface DisclosureProps {
  /** Controlled state; omit for an uncontrolled disclosure. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** The always-visible trigger content. */
  summary: React.ReactNode;
  /** Classes for the trigger button. */
  triggerClassName?: string;
  /** Classes for the panel's inner content wrapper. */
  panelClassName?: string;
  className?: string;
  /** Rendered before the summary; receives the rotation via a data attribute. */
  caret?: React.ReactNode;
  /** Interactive controls in the header row, after the trigger and OUTSIDE
   *  it, so using one never toggles the panel or joins the trigger's name. */
  actionsSlot?: React.ReactNode;
  /** Drop the panel's children once the collapse has finished, and mount them
   *  again on open. Default `false`: the panel stays mounted (ADR-0091). */
  unmountOnClose?: boolean;
  children: React.ReactNode;
}

export function Disclosure({
  open,
  defaultOpen = false,
  onOpenChange,
  summary,
  triggerClassName,
  panelClassName,
  className,
  caret,
  actionsSlot,
  unmountOnClose = false,
  children,
}: DisclosureProps) {
  const hasActions = actionsSlot != null;
  const trigger = (
    <DisclosureTrigger
      caret={caret}
      className={
        hasActions ? cn("min-w-0 flex-1", triggerClassName) : triggerClassName
      }
    >
      {summary}
    </DisclosureTrigger>
  );

  return (
    <DisclosureRoot
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
    >
      <div className={className}>
        {/* No actions: the button stays the root's direct child, as before. */}
        {hasActions ? (
          <div className="flex items-center gap-2">
            {trigger}
            <div className="flex shrink-0 items-center gap-2">
              {actionsSlot}
            </div>
          </div>
        ) : (
          trigger
        )}
        <DisclosurePanel
          className={panelClassName}
          unmountOnClose={unmountOnClose}
        >
          {children}
        </DisclosurePanel>
      </div>
    </DisclosureRoot>
  );
}
